"use client";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import menuData from "./menuData";

const Header = () => {
	const [navbarOpen, setNavbarOpen] = useState(false);
	const [sticky, setSticky] = useState(false);
	const [openIndex, setOpenIndex] = useState(-1);
	const pathname = usePathname();
	const [activePath, setActivePath] = useState(pathname);

	const navbarToggleHandler = () => {
		setNavbarOpen((prev) => !prev);
	};

	const closeNavbar = () => {
		setNavbarOpen(false);
		setOpenIndex(-1);
	};

	const handleStickyNavbar = () => {
		setSticky(window.scrollY >= 80);
	};

	useEffect(() => {
		handleStickyNavbar();
		window.addEventListener("scroll", handleStickyNavbar);
		return () => window.removeEventListener("scroll", handleStickyNavbar);
	}, []);

	useEffect(() => {
		setActivePath(pathname);
	}, [pathname]);

	const handleSubmenu = (index: number) => {
		setOpenIndex((prev) => (prev === index ? -1 : index));
	};

	const handleClick = (path?: string) => {
		if (!path) return;
		setActivePath(path);
		closeNavbar();
	};

	return (
		<header
			className={`header left-0 top-0 z-40 w-full ${sticky
				? "fixed z-[9999] bg-saintiantblue !bg-opacity-80 shadow-sticky backdrop-blur-sm transition dark:bg-gray-dark dark:shadow-sticky-dark"
				: "absolute bg-saintiantblue"
				}`}
		>
			<div className="container relative h-[100px] px-4">
				<div className="flex h-full items-center justify-between gap-4">
					<div className="w-[130px] shrink-0 sm:w-[150px] md:w-[170px] lg:w-[190px]">
						<Link
							href="/"
							className={`header-logo block w-full ${sticky ? "py-5 lg:py-2" : "py-8"}`}
							onClick={closeNavbar}
						>
							<Image
								src="/images/blog/Spandalogo.png"
								alt="Saintiant logo"
								width={190}
								height={60}
								priority
								sizes="(max-width: 640px) 130px, (max-width: 768px) 150px, (max-width: 1024px) 170px, 190px"
								className="h-auto w-full object-contain dark:hidden"
							/>
							<Image
								src="/images/logo/logo2.png"
								alt="Saintiant logo"
								width={140}
								height={30}
								priority
								sizes="140px"
								className="hidden h-auto w-full object-contain dark:block"
							/>
						</Link>
					</div>

					<button
						onClick={navbarToggleHandler}
						id="navbarToggler"
						aria-label="Mobile Menu"
						className="relative z-50 ml-auto block rounded-lg p-2 text-white focus:outline-none lg:hidden"
					>
						<span
							className={`my-1.5 block h-0.5 w-[28px] bg-white transition-all duration-300 ${navbarOpen ? "translate-y-2 rotate-45" : ""}`}
						/>
						<span
							className={`my-1.5 block h-0.5 w-[28px] bg-white transition-all duration-300 ${navbarOpen ? "opacity-0" : ""}`}
						/>
						<span
							className={`my-1.5 block h-0.5 w-[28px] bg-white transition-all duration-300 ${navbarOpen ? "-translate-y-2 -rotate-45" : ""}`}
						/>
					</button>

					<nav
						id="navbarCollapse"
						className={`navbar absolute inset-x-4 top-[calc(100%+12px)] z-40 overflow-hidden rounded-xl border border-slate-200 bg-white p-4 shadow-2xl transition-all duration-300 lg:visible lg:static lg:inset-auto lg:block lg:w-auto lg:border-none lg:bg-transparent lg:p-0 lg:shadow-none lg:overflow-visible ${navbarOpen
							? "visible translate-y-0 opacity-100"
							: "invisible -translate-y-2 opacity-0 lg:visible lg:translate-y-0 lg:opacity-100"
							}`}
					>
						<ul className="flex flex-col gap-1 lg:flex-row lg:items-center lg:gap-8">
							{menuData.map((menuItem, index) => (
								<li key={index} className="group relative">
									{menuItem.path ? (
										<Link
											href={menuItem.path ?? "/"}
											className={`flex whitespace-nowrap py-2 text-base lg:mr-0 lg:inline-flex lg:px-0 lg:py-6 ${activePath === menuItem.path
												? "text-blue-500 dark:text-blue-500"
												: "text-slate-800 hover:text-slate-600 lg:text-white lg:hover:text-slate-200"
												}`}
											onClick={() => handleClick(menuItem.path ?? "/")}
										>
											{menuItem.title}
										</Link>
									) : (
										<>
											<p
												onClick={() => handleSubmenu(index)}
												className="flex cursor-pointer items-center justify-between py-2 text-base text-slate-800 hover:text-slate-600 lg:text-white lg:hover:text-slate-200"
											>
												{menuItem.title}
												<span className="pl-3">
													<svg width="20" height="20" viewBox="0 0 25 24" className="inline-block">
														<path
															fillRule="evenodd"
															clipRule="evenodd"
															d="M6.29289 8.8427C6.68342 8.45217 7.31658 8.45217 7.70711 8.8427L12 13.1356L16.2929 8.8427C16.6834 8.45217 17.3166 8.45217 17.7071 8.8427C18.0976 9.23322 18.0976 9.86639 17.7071 10.2569L12 15.964L6.29289 10.2569C5.90237 9.86639 5.90237 9.23322 6.29289 8.8427Z"
															fill="currentColor"
														/>
													</svg>
												</span>
											</p>
											<div className={`submenu mt-2 rounded-lg bg-slate-50 p-2 transition-all lg:absolute lg:left-0 lg:top-[110%] lg:min-w-[220px] lg:p-3 lg:shadow-lg lg:opacity-0 lg:invisible lg:group-hover:opacity-100 lg:group-hover:visible ${openIndex === index ? "block" : "hidden lg:block"}`}
											>
												{menuItem.submenu?.map((submenuItem, subIndex) => (
													<Link
														href={submenuItem?.path ?? "#"}
														key={subIndex}
														className={`block rounded px-3 py-2 text-sm ${activePath === submenuItem.path
															? "text-blue-500 dark:text-blue-500"
															: "text-slate-700 hover:bg-slate-100 hover:text-slate-900 lg:text-slate-700"
															}`}
														onClick={() => handleClick(submenuItem.path ?? "/")}
													>
														{submenuItem?.title}
													</Link>
												))}
											</div>
										</>
									)}
								</li>
							))}
							<li className="pt-2 lg:pt-0">
								<Link
									href="/contact"
									className="inline-flex whitespace-nowrap rounded-md bg-saintiantgreen px-4 py-2.5 text-base font-semibold text-white transition-colors hover:bg-saintiantgreen/80"
									onClick={closeNavbar}
								>
									Schedule a Demo
								</Link>
							</li>
						</ul>
					</nav>
				</div>
			</div>
		</header>
	);
};

export default Header;
